<template>
  <div>
    <ModalViewSms id="modal-view-sms" :message="selectedSms" />
    <b-row class="mt-4 mb-1">
      <b-col>
        <p style="max-width: 690px">
          Detect spam SMS messages from any customer and user within a specific
          timeframe based on a minimum number of duplicate messages sent.
        </p>
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col cols="12" md="6" lg="5" xl="4" class="mb-sm-2">
        <R-Label :theme="labelTheme" value="Date and time range (UTC):" />
        <div class="py-1">
          <DateTimeRangeSelector
            class="w-100"
            :dateTimeRange="selectedDateTime"
            @onRangeChange="onRangeChange"
            :predefinedRange="selectedPredefinedRange"
            :predefinedRanges="predefinedRanges"
            :isDisabled="showLoader"
            :hasError="dateError"
            :theme="{ width: '100%' }"
            timeFormat="HH:mm"
            finishButtonLabel="Select"
            clearButtonLabel="Close"
            placeholder="Select a day"
          />
          <span v-if="dateError" class="small" style="color: rgb(225, 45, 45)"
            >Interval must be smaller than 1 day.</span
          >
        </div>
      </b-col>
      <b-col cols="12" md="6" lg="4" xl="3">
        <R-Label :theme="labelTheme" value="Minimum duplicate count:" />
        <R-Input
          :value="thresholdCount"
          @onChange="onThresholdChange"
          :isDisabled="showLoader"
          type="number"
          placeholder="value to set"
        />
      </b-col>
      <b-col>
        <R-Label :theme="labelTheme" value="" />
        <div class="d-flex flex-row align-items-center">
          <R-Button
            @onClick="retrieveSmsDiagnostics"
            class="mt-1 mr-2 fill-light"
            :isDisabled="showLoader"
          >
            <RefreshIcon style="transform: translateY(-2px)" />
          </R-Button>
          <R-Tooltip
            enableArrow
            :showOn="'click'"
            :orientation="'top'"
            :title="'For data privacy reasons, we do not disclose the body of the message unless you click the SMS cell in the table which will track your action in the audit trail. All dates selected and displayed are in the UTC timezone.'"
          >
            <InfoCircleSmallIcon
              size="26"
              class="mr-2 cursor-pointer"
              style="opacity: 0.7"
            />
          </R-Tooltip>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="12" class="mt-3">
        <table-view
          ref="table"
          v-model="getSmsDiagnostics"
          :visibleColumns="visibleColumns"
          :columnTitles="columnTitles"
          :formatter-cell="formatterCell"
          :isEditable="false"
          :hasSubmit="false"
          :showLoader="showLoader"
          :hasEditActionButtons="false"
          :hasSetActionButtons="false"
          :hasDownload="true"
          :download-accessors="downloadAccessors"
          @cellClick="cellClicked"
        >
        </table-view>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import TableView from "@/vue/components/tabulator/Index.vue";
import ModalViewSms from "@/vue/views/tools/smsaudit/ModalViewSms";
import Datepicker from "vuejs-datepicker";
import moment from "moment";
import debounce from "lodash/debounce";
import DateTimeRangeSelector from "@8x8/oxygen-date-time-range-selector";
import InfoCircleSmallIcon from "@8x8/oxygen-icon/icons/InfoCircleSmallIcon";
import { notify, DURATION, TYPE } from "@8x8/oxygen-toaster";
import Button from "@8x8/oxygen-button";
import Tooltip from "@8x8/oxygen-tooltip";

import { humanizeFromSeconds } from "@/vue/utils/time";

import { processDiagnostics, reorderAttributesForTable } from "./diagnostics";
import { predefinedRanges } from "./predefinedDateTimeRanges";

/**
 * Accessors change the way that values are exported to files in Tabulator.
 */
const downloadAccessors = Object.freeze({
  createdTime: {
    accessorDownload: (value, _data, _type, _params, _column) =>
      humanizeFromSeconds(value),
  },
});

const columns = [
  { name: "phoneNumber", label: "Phone Number" },
  { name: "duplicateCount", label: "Duplicate Count" },
  { name: "smsId", label: "SMS" },
  { name: "customerName", label: "Customer Name" },
  { name: "customerId", label: "Customer Id" },
  { name: "createdTime", label: "Customer Created At" },
  { name: "customerStatus", label: "Customer Status" },
  { name: "classType", label: "Class Type" },
  { name: "productFamily", label: "Product Family" },
  { name: "timeZoneId", label: "timeZoneId" },
  { name: "accountType", label: "Account Type" },
  { name: "firstName", label: "First Name" },
  { name: "lastName", label: "Last Name" },
  { name: "primaryEmail", label: "Primary Email" },
  { name: "primaryExtension", label: "Primary Extension" },
  { name: "userStatus", label: "User Status" },
  { name: "loginId", label: "Login Id" },
  { name: "userId", label: "User Id" },
  { name: "suspendedBy", label: "Suspended By" },
];

export default {
  name: "SmsDiagnostics",
  components: {
    ModalViewSms,
    Datepicker,
    TableView,
    DateTimeRangeSelector,
    InfoCircleSmallIcon,
    RButton: Button,
    RTooltip: Tooltip,
  },
  data() {
    return {
      selectedSms: null,
      selectedDateTime: undefined,
      selectedPredefinedRange: { id: "TODAY" },
      // no less than 20 or 10, not more than 1000
      thresholdCount: 100,
      dateError: null,
      showLoader: false,
      columns,
      downloadAccessors,
      labelTheme: { fontWeight: 600 },
    };
  },
  computed: {
    ...mapGetters(["getCustomer"]),
    getSmsDiagnostics() {
      const diagnostics = this.$store.state.smsaudit.smsDiagnostics || [];
      const processedDiagnostics = processDiagnostics(diagnostics);
      return reorderAttributesForTable(columns, processedDiagnostics);
    },
    predefinedRanges() {
      return predefinedRanges;
    },
    columnTitles() {
      const colTitles = {};
      columns.forEach(({ name, label }) => (colTitles[name] = label));
      console.log(colTitles);
      return colTitles;
    },
    visibleColumns() {
      return columns.map((c) => c.name);
    },
    fieldToClickActions() {
      return {
        smsId: this.handleViewMessage,
        customerId: this.copyToClipboard,
      };
    },
  },
  watch: {
    selectedDateTime(val) {
      if (!val) {
        this.dateError = null;
        return;
      }
      const { startDate, endDate } = val;
      const diff = moment(endDate).diff(moment(startDate), "days", true);
      this.dateError = diff > 1;
    },
  },
  mounted() {
    if (!this.getSmsDiagnostics.length) {
      this.retrieveSmsDiagnostics();
    }

    // Also re-fetch if the flag is set in the state. A re-fetch could be deemed necessary if
    // a whitelist operation has been performed.
    const shouldRefetchDiagnostics =
      this.$store.state.smsaudit.shouldRefetchDiagnostics;
    if (shouldRefetchDiagnostics) {
      notify({
        content: `Refreshing diagnostics after whitelist operations...`,
        type: "success",
        duration: 3000,
      });

      this.retrieveSmsDiagnostics();
      this.setShouldRefetchDiagnostics(false);
    }
  },
  methods: {
    ...mapActions([
      "fetchSmsDiagnostics",
      "getSmsBody",
      "setShouldRefetchDiagnostics",
    ]),
    retrieveSmsDiagnostics: debounce(async function () {
      if (this.dateError) {
        return;
      }

      this.showLoader = true;

      try {
        this.showLoader = true;
        await this.fetchSmsDiagnostics({
          ...this.timePayload(),
          duplicateThreshold: this.thresholdCount,
        });
      } catch (errorMessage) {
        notify({
          content: errorMessage,
          type: TYPE.error,
          duration: DURATION.INFINITE,
        });
      } finally {
        this.showLoader = false;
      }
    }, 1000),
    onRangeChange({ dateTime, predefinedRange } = {}) {
      if (predefinedRange) {
        const { id } = predefinedRange;
        this.selectedDateTime =
          id === "CUSTOM"
            ? { startDate: new Date(), endDate: new Date() }
            : undefined;
        this.selectedPredefinedRange = predefinedRange;
        this.retrieveSmsDiagnostics();
        return;
      }

      if (!dateTime) {
        return;
      }

      this.selectedDateTime = dateTime;
      this.selectedPredefinedRange = undefined;
      this.retrieveSmsDiagnostics();
    },
    onThresholdChange(event) {
      this.thresholdCount = event.target.value;
      this.retrieveSmsDiagnostics();
    },
    formatterCell(cell) {
      const key = cell.getField();
      const value = cell.getValue();

      // Hide the body by default and add a button to View that triggers audit trail
      if (key === "smsId" && value) {
        return `<span class="btn-link">View Message</span>`;
      }

      if (key === "customerId" && value) {
        return `<code>${value}</code>&nbsp;<span class="btn-link">Copy</span>`;
      }

      // Format customer creation time (in UTC) from UNIX timestamp
      if (key === "createdTime") {
        return humanizeFromSeconds(value);
      }

      return value;
    },
    async cellClicked(event, cell) {
      const field = cell.getField();
      const action = this.fieldToClickActions?.[field];
      if (!action) {
        return;
      }
      const data = cell.getData();
      await action(data);
    },
    async handleViewMessage(data) {
      const { smsId, phoneNumber, customerId } = data;
      try {
        this.showLoader = true;
        this.selectedSms = await this.getSmsBody({
          smsId,
          phoneNumber,
          customerId,
        });
        this.$bvModal.show("modal-view-sms");
      } catch (e) {
        notify({
          content: e.message || e,
          type: TYPE.error,
          duration: DURATION.INFINITE,
        });
        this.showLoader = false;
      } finally {
        this.showLoader = false;
      }
    },
    async copyToClipboard(data) {
      let content, type;
      const { customerId } = data;

      try {
        await navigator.clipboard.writeText(customerId);

        type = TYPE.success;
        content = "Copied customer ID to clipboard.";
      } catch (exception) {
        type = TYPE.error;
        content = exception?.message ?? JSON.stringify(exception);
      }

      notify({ content, type, duration: DURATION.INFINITE });
    },
    timePayload() {
      let startDate;
      let endDate;

      if (this.selectedDateTime) {
        startDate = this.selectedDateTime.startDate;
        endDate = this.selectedDateTime.endDate;
      }

      if (this.selectedPredefinedRange) {
        const { id, value } = this.selectedPredefinedRange;
        const range = predefinedRanges
          .find((r) => r.id === id)
          .dateTimeRange(value);
        startDate = range.startDate;
        endDate = range.endDate;
      }

      return {
        // Get the millisecond-resolution timestamp for the dates after the local timezone has been removed.
        // utc(true) keeps the exact timestamp but removes any local timezone data.
        startTime: moment(startDate).utc(true).valueOf(),
        stopTime: moment(endDate).utc(true).valueOf(),
      };
    },
  },
};
</script>
